import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Stack,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { getCareTeam, setCareTeam, updateCareTeam, deleteCareTeam, getAllCareTeams } from '../../services/careCoordinationService';

const roleOptions = [
  'PRIMARY_CARE',
  'SPECIALIST',
  'NURSE',
  'CARE_COORDINATOR',
  'SOCIAL_WORKER',
  'PHARMACIST',
  'THERAPIST'
];

export default function CareTeamTab({ patientId, patients }) {
  const [careTeams, setCareTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadCareTeams = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = patientId 
        ? await getCareTeam(patientId)
        : await getAllCareTeams();
      setCareTeams(data ? (Array.isArray(data) ? data : [data]) : []);
    } catch (err) {
      setError('Failed to load care teams');
      console.error('Error loading care teams:', err);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    loadCareTeams();
  }, [patientId, loadCareTeams]);

  // Helper to get patient name by id
  const getPatientName = (id) => {
    console.log('CareTeamTab: getPatientName called with:', {
      patientId: id,
      patientsAvailable: patients?.length || 0,
      patients: patients
    });
    
    if (!id || !patients || patients.length === 0) return 'Unknown Patient';
    
    // Try different ways to find the patient
    let p = patients.find(p => p.patientId === id);
    if (!p) p = patients.find(p => p.id === id);
    if (!p) p = patients.find(p => p._id === id);
    
    console.log('CareTeamTab: Patient lookup result:', {
      patientId: id,
      foundPatient: p,
      lookupMethods: {
        byPatientId: patients.find(p => p.patientId === id),
        byId: patients.find(p => p.id === id),
        by_id: patients.find(p => p._id === id)
      }
    });
    
    if (p) {
      // Handle different patient name structures
      if (p.firstName && p.lastName) {
        return `${p.firstName} ${p.lastName}`;
      } else if (p.name) {
        return p.name;
      } else if (p.firstName) {
        return p.firstName;
      } else if (p.lastName) {
        return p.lastName;
      }
    }
    
    // If we can't find the patient, show a more user-friendly message
    return `Patient ${id.substring(0, 8)}...`;
  };

  const handleAdd = () => {
    setSelectedTeam({
      patientId: patientId || '',
      name: '',
      members: [{
        name: '',
        role: 'PRIMARY_CARE',
        contact: '',
        isCoordinator: true,
        status: 'ACTIVE'
      }]
    });
    setIsDialogOpen(true);
  };

  const handleEdit = (team) => {
    // Ensure the team has a members array, even if it's empty
    const teamWithMembers = {
      ...team,
      members: team.members || []
    };
    
    // If no members exist, add a default member
    if (teamWithMembers.members.length === 0) {
      teamWithMembers.members = [{
        name: '',
        role: 'PRIMARY_CARE',
        contact: '',
        isCoordinator: true,
        status: 'ACTIVE'
      }];
    }
    
    setSelectedTeam(teamWithMembers);
    setIsDialogOpen(true);
  };

  const handleDelete = async (team) => {
    if (!window.confirm('Are you sure you want to delete this care team?')) return;
    
    try {
      if (team.id) {
        // Use the new DELETE endpoint if we have a team ID
        await deleteCareTeam(team.id);
      } else {
        // Fallback to the old method if no team ID (for backward compatibility)
        await setCareTeam(team.patientId, []);
      }
      await loadCareTeams();
    } catch (err) {
      setError('Failed to delete care team');
      console.error('Error deleting care team:', err);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');

      // Ensure members array exists
      if (!selectedTeam.members || selectedTeam.members.length === 0) {
        setError('Care team must have at least one member');
        return;
      }

      // Ensure at least one coordinator
      const hasCoordinator = selectedTeam.members.some(m => m.isCoordinator);
      if (!hasCoordinator) {
        setError('Care team must have at least one coordinator');
        return;
      }

      if (selectedTeam.id) {
        // Update existing care team using the new PUT endpoint
        await updateCareTeam(selectedTeam.id, {
          members: selectedTeam.members,
          name: selectedTeam.name,
          description: selectedTeam.description,
          status: selectedTeam.status
        });
      } else {
        // Create new care team using the existing POST endpoint
        await setCareTeam(
          selectedTeam.patientId,
          selectedTeam.members
        );
      }
      setIsDialogOpen(false);
      await loadCareTeams();
    } catch (err) {
      setError('Failed to save care team');
      console.error('Error saving care team:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = () => {
    setSelectedTeam({
      ...selectedTeam,
      members: [
        ...(selectedTeam.members || []),
        {
          name: '',
          role: 'PRIMARY_CARE',
          contact: '',
          isCoordinator: false,
          status: 'ACTIVE'
        }
      ]
    });
  };

  const handleRemoveMember = (index) => {
    const updatedMembers = [...(selectedTeam.members || [])];
    updatedMembers.splice(index, 1);
    setSelectedTeam({
      ...selectedTeam,
      members: updatedMembers
    });
  };

  const handleMemberChange = (index, field, value) => {
    const updatedMembers = [...(selectedTeam.members || [])];
    updatedMembers[index] = {
      ...updatedMembers[index],
      [field]: value
    };
    setSelectedTeam({
      ...selectedTeam,
      members: updatedMembers
    });
  };

  const handleSetCoordinator = async (team, memberIndex) => {
    try {
      // Ensure team has members
      if (!team.members || team.members.length === 0) {
        setError('Cannot set coordinator for team without members');
        return;
      }

      const updatedMembers = team.members.map((member, idx) => ({
        ...member,
        isCoordinator: idx === memberIndex
      }));

      if (team.id) {
        // Use the new PUT endpoint if we have a team ID
        await updateCareTeam(team.id, { members: updatedMembers });
      } else {
        // Fallback to the old method if no team ID (for backward compatibility)
        await setCareTeam(team.patientId, updatedMembers);
      }
      await loadCareTeams();
    } catch (err) {
      setError('Failed to update coordinator');
      console.error('Error updating coordinator:', err);
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <h2>Care Teams</h2>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAdd}
        >
          Add Care Team
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography>Loading care teams...</Typography>
        </Box>
      ) : careTeams.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="textSecondary" gutterBottom>
            No care teams found
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Click "Add Care Team" to create your first care team
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Patient</TableCell>
                <TableCell>Team Name</TableCell>
                <TableCell>Coordinator</TableCell>
                <TableCell>Provider ID</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Facility</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {careTeams.map((team) => {
                // Handle care teams that don't have members array yet
                const members = team.members || [];
                
                if (members.length === 0) {
                  // Show a placeholder row for teams without members
                                     return (
                     <TableRow key={`${team.id}-no-members`}>
                       <TableCell>{getPatientName(team.patientId)}</TableCell>
                       <TableCell>{team.name || 'Unnamed Team'}</TableCell>
                       <TableCell>
                         <Typography variant="body2" color="textSecondary">
                           No members assigned
                         </Typography>
                       </TableCell>
                       <TableCell>N/A</TableCell>
                       <TableCell>N/A</TableCell>
                       <TableCell>{team.facilityId || 'N/A'}</TableCell>
                       <TableCell>{team.status || 'N/A'}</TableCell>
                       <TableCell align="right">
                         <Tooltip title="Edit Team">
                           <IconButton onClick={() => handleEdit(team)}>
                             <EditIcon />
                           </IconButton>
                         </Tooltip>
                         <Tooltip title="Delete Team">
                           <IconButton onClick={() => handleDelete(team)}>
                             <DeleteIcon />
                           </IconButton>
                         </Tooltip>
                       </TableCell>
                     </TableRow>
                   );
                }
                
                // Show rows for teams with members
                return members.map((member, mIdx) => {
                  console.log('CareTeamTab: Processing team member:', {
                    teamId: team.id,
                    patientId: team.patientId,
                    member: member
                  });
                  
                                     return (
                     <TableRow key={`${team.id}-${mIdx}`}>
                       <TableCell>{getPatientName(team.patientId)}</TableCell>
                       <TableCell>{team.name || 'Unnamed Team'}</TableCell>
                       <TableCell>
                         <Tooltip title={member.isCoordinator ? 'Care Coordinator' : 'Set as Coordinator'}>
                           <IconButton 
                             color={member.isCoordinator ? 'primary' : 'default'}
                             onClick={() => !member.isCoordinator && handleSetCoordinator(team, mIdx)}
                           >
                             {member.isCoordinator ? <StarIcon /> : <StarBorderIcon />}
                           </IconButton>
                         </Tooltip>
                       </TableCell>
                       <TableCell>{member.providerId || 'N/A'}</TableCell>
                       <TableCell>{member.role || 'N/A'}</TableCell>
                       <TableCell>{member.facilityId || 'N/A'}</TableCell>
                       <TableCell>{member.status || 'N/A'}</TableCell>
                       <TableCell align="right">
                         <Tooltip title="Edit Team">
                           <IconButton onClick={() => handleEdit(team)}>
                             <EditIcon />
                           </IconButton>
                         </Tooltip>
                         <Tooltip title="Delete Team">
                           <IconButton onClick={() => handleDelete(team)}>
                             <DeleteIcon />
                           </IconButton>
                         </Tooltip>
                       </TableCell>
                     </TableRow>
                   );
                });
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog 
        open={isDialogOpen} 
        onClose={() => setIsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedTeam?.id ? 'Edit Care Team' : 'New Care Team'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {!patientId && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Patient</InputLabel>
                <Select
                  value={selectedTeam?.patientId || ''}
                  onChange={(e) => setSelectedTeam({
                    ...selectedTeam,
                    patientId: e.target.value
                  })}
                  label="Patient"
                >
                  {patients?.map((patient) => (
                    <MenuItem key={patient.id} value={patient.id}>
                      {`${patient.firstName} ${patient.lastName}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {selectedTeam?.members?.map((member, index) => (
              <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid #eee', borderRadius: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">Team Member {index + 1}</Typography>
                  {(selectedTeam.members || []).length > 1 && (
                    <IconButton 
                      color="error" 
                      onClick={() => handleRemoveMember(index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Stack>

                <TextField
                  fullWidth
                  label="Name"
                  value={member.name}
                  onChange={(e) => handleMemberChange(index, 'name', e.target.value)}
                  sx={{ mb: 2 }}
                />

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={member.role}
                    onChange={(e) => handleMemberChange(index, 'role', e.target.value)}
                    label="Role"
                  >
                    {roleOptions.map((role) => (
                      <MenuItem key={role} value={role}>
                        {role.replace('_', ' ')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Contact"
                  value={member.contact}
                  onChange={(e) => handleMemberChange(index, 'contact', e.target.value)}
                  sx={{ mb: 2 }}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={member.isCoordinator}
                      onChange={(e) => handleMemberChange(index, 'isCoordinator', e.target.checked)}
                    />
                  }
                  label="Care Coordinator"
                />
              </Box>
            ))}

            <Button
              variant="outlined"
              onClick={handleAddMember}
              startIcon={<AddIcon />}
            >
              Add Team Member
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={loading}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 